import { DateTime } from "luxon"
import { CalendarApi, EventApi, EventDropArg, EventMountArg } from "@fullcalendar/core"
import { EventResizeDoneArg } from "@fullcalendar/interaction"
import { TimelineCalendarEvent, TimelineCalendarResource } from "./fullCalendarTypes"
import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { ResourceScheduleItem } from "../../../createOrderTypes"

export const BUFFER_SIZE_ATTR = "--buffer-size"
export const SLOT_SIZE = 26
export const SLOT_DURATION = 15

const FREE_SLOT_EVENT_COLOR = "#93C5FD"

export function generateCalendarEvents(
    resources: ResourceScheduleItem[],
    intervalMinutes: number,
    dateTimeFactory: DateTimeFactory,
): TimelineCalendarEvent[] {
    const events: TimelineCalendarEvent[] = []

    for (const { resource, availability } of resources) {
        for (const entry of availability) {
            let slotStart = dateTimeFactory.fromISO(entry.startTime)
            let intervalEnd = dateTimeFactory.fromISO(entry.endTime)

            let isCurrentSlotFree = entry.availabilityView[0] === "0"
            let currentFreeSlotStart: DateTime | null = isCurrentSlotFree ? slotStart : null

            for (let i = 1; i <= entry.availabilityView.length; i++) {
                const isNextSlotFree = entry.availabilityView[i] === "0"
                const nextSlotStart = slotStart.plus({ minutes: intervalMinutes })

                if (!isNextSlotFree && isCurrentSlotFree) {
                    events.push({
                        id: `free-${resource.uuid}-${currentFreeSlotStart!.toMillis()}-${i}`,
                        resourceIds: [resource.uuid],
                        start: currentFreeSlotStart!.toISO() || "",
                        end: nextSlotStart.toISO() || "",
                        backgroundColor: FREE_SLOT_EVENT_COLOR,
                    })
                    currentFreeSlotStart = null
                }

                if (isNextSlotFree && !isCurrentSlotFree) {
                    currentFreeSlotStart = nextSlotStart
                }

                if (i === entry.availabilityView.length && currentFreeSlotStart) {
                    events.push({
                        id: `free-${resource.uuid}-${currentFreeSlotStart.toMillis()}`,
                        resourceIds: [resource.uuid],
                        start: currentFreeSlotStart.toISO() || "",
                        end: intervalEnd.toISO() || "",
                        subject: "Available",
                        canEdit: false,
                        outOfService: false,
                        backgroundColor: FREE_SLOT_EVENT_COLOR,
                    })
                }

                isCurrentSlotFree = isNextSlotFree
                slotStart = nextSlotStart
            }
        }
    }

    return events
}

export const getRoundedScrollTime = () => {
    const now = DateTime.local()
    const minusOneHour = now.minus({ hours: 1 })

    const boundedTime = minusOneHour < now.startOf("day") ? now.startOf("day") : minusOneHour

    const roundedMinutes = Math.floor(boundedTime.minute / 15) * 15

    const adjustedTime = boundedTime.set({
        minute: roundedMinutes,
        second: 0,
    })

    return adjustedTime.toFormat("HH:mm")
}

export const isInPast = (iso: string | undefined | null): boolean => {
    if (!iso) return false
    return DateTime.fromISO(iso).diffNow().as("seconds") < 0
}

export function forceFullRevert(info: EventDropArg | EventResizeDoneArg) {
    const { oldEvent, view } = info

    info.revert()
    view.calendar.addEvent({ ...oldEvent })
}

export const applyEventBufferStyle = (info: EventMountArg, resources: TimelineCalendarResource[]) => {
    const currentResourceId = info.el.closest("[data-resource-id]")?.getAttribute("data-resource-id")
    const resource = resources.find((res) => res.id === currentResourceId)
    const bufferForResource = resource?.buffer || 0

    info.el.style.setProperty(BUFFER_SIZE_ATTR, `${(bufferForResource / SLOT_DURATION) * SLOT_SIZE}px`)
}

type HasDateRange = Pick<EventApi, "start" | "end"> | Pick<TimelineCalendarEvent, "start" | "end">
const toDateTime = (value: string | Date, dateTimeFactory: DateTimeFactory) =>
    typeof value === "string" ? dateTimeFactory.fromISO(value) : DateTime.fromJSDate(value)

export const isOngoing = (event: HasDateRange, dateTimeFactory: DateTimeFactory): boolean => {
    if (!event.start || !event.end) return false

    const now = dateTimeFactory.now()
    const start = toDateTime(event.start, dateTimeFactory)
    const end = toDateTime(event.end, dateTimeFactory)

    return start.isValid && end.isValid && start <= now && now < end
}

export const isSameResources = (a: readonly string[], b: readonly string[]) => {
    const setA = new Set(a)
    return b.every((id) => setA.has(id))
}

export async function scrollToResource(resourceId?: string): Promise<void> {
    if (!resourceId) return

    const scroller = document.querySelector<HTMLElement>(".fc-scroller.fc-scroller-liquid-absolute")
    if (!scroller) return

    const selector = `[data-resource-id="${resourceId}"]`

    const tryScroll = () => {
        const target = document.querySelector<HTMLElement>(selector)
        if (!target) return false

        const scrollTop = target.offsetTop - scroller.clientHeight / 2.5 + target.clientHeight

        let attempts = 0
        const maxAttempts = 7

        const animateScroll = () => {
            scroller.scrollTo({ top: scrollTop, behavior: "smooth" })
            attempts++
            if (attempts < maxAttempts) {
                requestAnimationFrame(animateScroll)
            }
        }

        requestAnimationFrame(animateScroll)
        return true
    }

    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            if (tryScroll()) {
                setTimeout(resolve, 300)
                return
            }

            const observer = new MutationObserver(() => {
                if (tryScroll()) {
                    observer.disconnect()
                    setTimeout(resolve, 300)
                }
            })

            observer.observe(scroller, { childList: true, subtree: true })

            setTimeout(() => {
                observer.disconnect()
                resolve()
            }, 3000)
        })
    })
}

export function applyDynamicTimeBuffer(calendarApi: CalendarApi, start: DateTime) {
    calendarApi.setOption("slotMinTime", start.toFormat("HH:mm:ss"))
    calendarApi.setOption("slotMaxTime", `${start.hour + 24}:00:00`)
    calendarApi.setOption("duration", { days: 2 })
}
